// Wishlist Management System - Variant-aware with Shopify Customer Metafield Sync + Klaviyo Events
(function() {
  'use strict';

  const WISHLIST_API_URL = 'https://wishlist-sync-function.vercel.app/api';

  class WishlistManager {
    constructor() {
      this.storageKey = 'wishlist';
      this.syncInProgress = false;
      this.syncTimeout = null;
    }

    async init() {
      this.migrateOldWishlist();
      this.ensureWishlistExists();
      await this.loadFromShopify();
      
      // If user is logged in and has items in localStorage, sync to Shopify
      if (this.isLoggedIn() && this.getCount() > 0) {
        setTimeout(() => {
          this.syncToShopify();
        }, 3000);
      }
      
      setTimeout(() => {
        this.updateAllCounts();
        this.updateAllButtons();
      }, 100);
    }

    getCustomerId() {
      // Check window.Shopify.customer first (from js-variables.liquid)
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
        return window.Shopify.customer.id.toString();
      }
      
      // Fallback to meta tag
      const metaTag = document.querySelector('meta[name="shopify-customer-id"]');
      if (metaTag) {
        const id = metaTag.getAttribute('content');
        if (id) {
          return id.toString();
        }
      }
      
      return null;
    }

    isLoggedIn() {
      return this.getCustomerId() !== null;
    }

    async loadFromShopify() {
      const customerId = this.getCustomerId();
      
      if (!customerId) {
        return;
      }
      
      try {
        const url = `${WISHLIST_API_URL}/get-wishlist?customerId=${encodeURIComponent(`gid://shopify/Customer/${customerId}`)}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.success && Array.isArray(result.wishlist) && result.wishlist.length > 0) {
          await this.mergeFromShopifyIds(result.wishlist);
        }
      } catch (error) {
        // Error loading from Shopify
      }
    }

    async mergeFromShopifyIds(shopifyItems) {
      const localWishlist = this.getWishlist();
      const mergedMap = new Map();
      
      shopifyItems.forEach(item => {
        if (item.handle) {
          mergedMap.set(item.handle, { handle: item.handle, variantId: null });
        }
      });
      
      localWishlist.forEach(item => {
        mergedMap.set(item.handle, item);
      });
      
      const mergedWishlist = Array.from(mergedMap.values());
      
      localStorage.setItem(this.storageKey, JSON.stringify(mergedWishlist));
      this.publishUpdate();
    }

    async syncToShopify() {
      const customerId = this.getCustomerId();
      
      if (!customerId || this.syncInProgress) {
        return;
      }

      this.syncInProgress = true;
      const wishlist = this.getWishlist();
      
      try {
        const productIds = await this.getProductIdsFromHandles(wishlist);
        
        if (productIds.length === 0) {
          return;
        }
        
        const response = await fetch(`${WISHLIST_API_URL}/sync-wishlist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: `gid://shopify/Customer/${customerId}`,
            wishlist: productIds
          })
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        // Retry after 5 seconds
        setTimeout(() => {
          if (this.isLoggedIn() && !this.syncInProgress) {
            this.syncToShopify();
          }
        }, 5000);
      } finally {
        this.syncInProgress = false;
      }
    }
    
    async getProductIdsFromHandles(wishlist) {
      const productIds = [];
      
      for (const item of wishlist) {
        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (response.ok) {
            const product = await response.json();
            productIds.push(product.id);
          }
        } catch (error) {
          // Failed to get product ID
        }
      }
      
      return productIds;
    }

    // =========================================================================
    // KLAVIYO: Fire-and-forget event naar /api/wishlist-event
    // =========================================================================

    sendKlaviyoEvent(productHandle, action) {
      const customerId = this.getCustomerId();
      if (!customerId) return;

      fetch(`${WISHLIST_API_URL}/wishlist-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          productHandle,
          action
        })
      }).catch(err => console.warn('[Wishlist] Klaviyo event failed:', err));
    }

    migrateOldWishlist() {
      const oldWishlist = localStorage.getItem(this.storageKey);
      if (oldWishlist) {
        try {
          const parsed = JSON.parse(oldWishlist);
          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
            const newWishlist = parsed.map(handle => ({ handle, variantId: null }));
            localStorage.setItem(this.storageKey, JSON.stringify(newWishlist));
          }
        } catch (e) {
          // Migration error
        }
      }
    }

    ensureWishlistExists() {
      if (!localStorage.getItem(this.storageKey)) {
        localStorage.setItem(this.storageKey, JSON.stringify([]));
      }
    }

    getWishlist() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || [];
      } catch (e) {
        return [];
      }
    }

    saveWishlist(wishlist) {
      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
      this.publishUpdate();
      
      if (this.isLoggedIn()) {
        clearTimeout(this.syncTimeout);
        this.syncTimeout = setTimeout(() => {
          this.syncToShopify();
        }, 2000);
      }
    }

    addItem(productHandle, variantId = null) {
      const wishlist = this.getWishlist();
      const existingIndex = wishlist.findIndex(item => item.handle === productHandle);
      
      if (existingIndex === -1) {
        wishlist.push({ handle: productHandle, variantId: variantId });
        this.saveWishlist(wishlist);
        this.sendKlaviyoEvent(productHandle, 'add');
        return true;
      } else if (variantId && wishlist[existingIndex].variantId !== variantId) {
        wishlist[existingIndex].variantId = variantId;
        this.saveWishlist(wishlist);
        return true;
      }
      return false;
    }

    removeItem(productHandle) {
      let wishlist = this.getWishlist();
      const index = wishlist.findIndex(item => item.handle === productHandle);
      if (index > -1) {
        wishlist.splice(index, 1);
        this.saveWishlist(wishlist);
        this.sendKlaviyoEvent(productHandle, 'remove');
        return true;
      }
      return false;
    }

    hasItem(productHandle) {
      return this.getWishlist().some(item => item.handle === productHandle);
    }

    getItem(productHandle) {
      return this.getWishlist().find(item => item.handle === productHandle) || null;
    }

    getCount() {
      return this.getWishlist().length;
    }

    getProductHandles() {
      return this.getWishlist().map(item => item.handle);
    }

    clear() {
      this.saveWishlist([]);
    }

    publishUpdate() {
      const event = new CustomEvent('wishlist:updated', {
        detail: {
          wishlist: this.getWishlist(),
          count: this.getCount()
        }
      });
      document.dispatchEvent(event);
      
      setTimeout(() => {
        this.updateAllCounts();
        this.updateAllButtons();
      }, 50);
    }

    updateAllCounts() {
      const counts = document.querySelectorAll('wishlist-count');
      counts.forEach(count => {
        if (count.update && typeof count.update === 'function') {
          count.update();
        }
      });
    }

    updateAllButtons() {
      const buttons = document.querySelectorAll('wishlist-button');
      buttons.forEach(button => {
        if (button.update && typeof button.update === 'function') {
          button.update();
        }
      });
    }

    // Manual sync function for debugging
    async manualSync() {
      await this.syncToShopify();
    }
  }

  // Expose manual sync to window for debugging
  window.manualWishlistSync = function() {
    if (window.WishlistManager) {
      window.WishlistManager.manualSync();
    }
  };

  class WishlistCount extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.update();
      document.addEventListener('wishlist:updated', () => this.update());
    }

    update() {
      if (!window.WishlistManager) return;
      
      const count = window.WishlistManager.getCount();
      
      if (count === 0) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      
      if (count > 99) {
        this.innerHTML = '<span class="text-sm-extra">99+</span>';
        this.classList.add('cart-count--small-medium');
      } else {
        this.textContent = count;
        this.classList.remove('cart-count--small-medium');
      }
    }
  }

  class WishlistButton extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.productHandle = this.dataset.productHandle;
      this.variantId = this.dataset.variantId || null;
      
      if (!this.productHandle) {
        return;
      }

      this.render();
      this.attachEventListeners();
      setTimeout(() => this.update(), 50);
    }

    attachEventListeners() {
      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      });

      document.addEventListener('wishlist:updated', () => this.update());
      
      if (this.classList.contains('product-page-wishlist-button')) {
        this.observeVariantChanges();
      }
    }

    observeVariantChanges() {
      const variantInput = document.querySelector('.product-variant-id');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          this.variantId = variantInput.value;
        });
        
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
        
        variantInput.addEventListener('change', () => {
          this.variantId = variantInput.value;
        });
      }
    }

    render() {
      if (this.hasRendered) return;
      
      this.classList.add('wishlist-button');
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-label', 'Add to wishlist');
      
      this.innerHTML = `
        <svg class="wishlist-button__icon" width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path class="wishlist-button__icon-outline" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="#000" stroke-width="2"/>
          <path class="wishlist-button__icon-filled" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#e74c3c"/>
        </svg>
      `;
      
      this.hasRendered = true;
    }

    toggle() {
      if (!window.WishlistManager) return;
      
      const isInWishlist = window.WishlistManager.hasItem(this.productHandle);
      
      if (isInWishlist) {
        window.WishlistManager.removeItem(this.productHandle);
      } else {
        const currentVariantId = this.getCurrentVariantId();
        window.WishlistManager.addItem(this.productHandle, currentVariantId);
        
        this.classList.add('wishlist-button--animating');
        setTimeout(() => {
          this.classList.remove('wishlist-button--animating');
        }, 600);
      }
    }

    getCurrentVariantId() {
      if (this.variantId) {
        return this.variantId;
      }
      
      const variantInput = document.querySelector('.product-variant-id');
      if (variantInput) {
        return variantInput.value;
      }
      
      return null;
    }

    update() {
      if (!window.WishlistManager) return;
      
      const isInWishlist = window.WishlistManager.hasItem(this.productHandle);
      
      if (isInWishlist) {
        this.classList.add('wishlist-button--active');
        this.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        this.classList.remove('wishlist-button--active');
        this.setAttribute('aria-label', 'Add to wishlist');
      }
    }
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCount);
  }
  
  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlist);
  } else {
    initWishlist();
  }

  async function initWishlist() {
    window.WishlistManager = new WishlistManager();
    await window.WishlistManager.init();
  }
})();
